import React, { useState, useEffect, useCallback } from 'react';
import {
  Paper, Typography, Table, TableHead, TableRow, TableCell, TableBody,
  Chip, IconButton, Tooltip, Box, CircularProgress, Select, MenuItem
} from '@mui/material';
import {
  Pause as PauseIcon,
  PlayArrow as ResumeIcon,
  Cancel as CancelIcon,
  Autorenew as RecurringIcon
} from '@mui/icons-material';
import logger from '../../utils/logger';

const STATUS_COLORS = { ACTIVE: 'success', PAUSED: 'default', PAST_DUE: 'error', CANCELLED: 'default' };

/**
 * Admin panel listing recurring billing schedules (saved-card auto-charges)
 * with pause / resume / cancel and charge-timing controls.
 * Rendered on the Payment Management page.
 */
const RecurringSchedulesPanel = () => {
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);

  const apiBase = process.env.REACT_APP_API_URL?.replace(/\/$/, '') || '';
  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const fetchSchedules = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${apiBase}/api/bookings/recurring-schedules`, { headers: authHeaders() });
      const data = await res.json();
      setSchedules(data.schedules || []);
    } catch (err) {
      logger.log('[RecurringSchedulesPanel] fetch failed:', err.message);
      setSchedules([]);
    } finally {
      setLoading(false);
    }
  }, [apiBase]);

  useEffect(() => { fetchSchedules(); }, [fetchSchedules]);

  const updateSchedule = async (id, body, confirmMsg) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    try {
      await fetch(`${apiBase}/api/bookings/recurring-schedules/${id}`, {
        method: 'PATCH',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      fetchSchedules();
    } catch (err) {
      logger.log('[RecurringSchedulesPanel] update failed:', err.message);
    }
  };

  const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : '—');

  return (
    <Paper sx={{ p: 2, mt: 3 }}>
      <Box display="flex" alignItems="center" gap={1} mb={1}>
        <RecurringIcon color="primary" />
        <Typography variant="h6">Recurring Billing Schedules</Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Saved-card auto-charges for recurring customers. Charges run 24h before or after each visit per the agreed terms.
      </Typography>

      {loading ? (
        <Box display="flex" justifyContent="center" py={3}><CircularProgress size={28} /></Box>
      ) : schedules.length === 0 ? (
        <Typography variant="body2" color="text.disabled">No recurring schedules yet.</Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Customer</TableCell>
              <TableCell>Frequency</TableCell>
              <TableCell align="right">Amount</TableCell>
              <TableCell>Next visit</TableCell>
              <TableCell>Next charge</TableCell>
              <TableCell>Timing</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {schedules.map((s) => {
              const client = s.booking?.client;
              return (
                <TableRow key={s.id} hover>
                  <TableCell>
                    <Typography variant="body2">{client ? `${client.firstName} ${client.lastName}` : '—'}</Typography>
                    <Typography variant="caption" color="text.secondary">{client?.email}</Typography>
                  </TableCell>
                  <TableCell sx={{ textTransform: 'capitalize' }}>{s.frequency.replace('-', ' ')}</TableCell>
                  <TableCell align="right">${(s.amountCents / 100).toFixed(2)}</TableCell>
                  <TableCell>{fmtDate(s.nextVisitDate)}</TableCell>
                  <TableCell>{fmtDate(s.nextChargeDate)}</TableCell>
                  <TableCell>
                    <Select
                      value={s.chargeTiming}
                      size="small"
                      variant="standard"
                      disabled={s.status === 'CANCELLED'}
                      onChange={(e) => updateSchedule(s.id, { chargeTiming: e.target.value })}
                    >
                      <MenuItem value="BEFORE_24H">24h before</MenuItem>
                      <MenuItem value="AFTER_24H">24h after</MenuItem>
                    </Select>
                  </TableCell>
                  <TableCell>
                    <Chip label={s.status} color={STATUS_COLORS[s.status] || 'default'} size="small" />
                    {s.status === 'PAST_DUE' && s.lastChargeError && (
                      <Tooltip title={s.lastChargeError}>
                        <Typography variant="caption" color="error" display="block">card issue</Typography>
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell>
                    {s.status === 'ACTIVE' && (
                      <Tooltip title="Pause charging">
                        <IconButton size="small" onClick={() => updateSchedule(s.id, { status: 'PAUSED' })}>
                          <PauseIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {(s.status === 'PAUSED' || s.status === 'PAST_DUE') && (
                      <Tooltip title="Resume charging (clears failures)">
                        <IconButton size="small" onClick={() => updateSchedule(s.id, { status: 'ACTIVE' })}>
                          <ResumeIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {s.status !== 'CANCELLED' && (
                      <Tooltip title="Cancel recurring service">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => updateSchedule(s.id, { status: 'CANCELLED' }, 'Cancel this recurring schedule? The customer will no longer be charged automatically.')}
                        >
                          <CancelIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </Paper>
  );
};

export default RecurringSchedulesPanel;
